import React, { useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../ui/input-otp';

const DEFAULT_COOLDOWN_SECONDS = 60;

export default function OtpVerificationForm({
  otp,
  onOtpChange,
  onSubmit,
  onResend,
  onCancel,
  submitting = false,
  resending = false,
  serverError = '',
  cooldownSeconds = DEFAULT_COOLDOWN_SECONDS,
  expiryMinutes = 10,
  submitLabel = 'Verify OTP',
  submittingLabel = 'Verifying…',
  showCancel = true,
  cancelLabel = 'Cancel',
}) {
  const [cooldown, setCooldown] = useState(cooldownSeconds);

  useEffect(() => {
    setCooldown(cooldownSeconds);
  }, [cooldownSeconds]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    await onResend?.();
    setCooldown(cooldownSeconds);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {serverError && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {serverError}
        </div>
      )}

      <div className="flex justify-center">
        <InputOTP maxLength={6} value={otp} onChange={onOtpChange} disabled={submitting}>
          <InputOTPGroup>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <InputOTPSlot key={i} index={i} className="h-11 w-11 text-lg" />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </div>

      <p className="text-center text-sm text-muted-foreground">
        Code expires in {expiryMinutes} minutes
      </p>

      <div className="flex flex-col gap-3">
        <Button type="submit" className="w-full" disabled={submitting || otp.length !== 6}>
          {submitting ? submittingLabel : submitLabel}
        </Button>

        <div className="flex gap-2">
          {showCancel && (
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onCancel}
              disabled={submitting}
            >
              {cancelLabel}
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            className={showCancel ? 'flex-1' : 'w-full'}
            disabled={cooldown > 0 || resending || submitting}
            onClick={handleResend}
          >
            {cooldown > 0 ? `Resend in ${cooldown}s` : resending ? 'Sending…' : 'Resend code'}
          </Button>
        </div>
      </div>
    </form>
  );
}

export function setOtpResendCooldown(setCooldown, retryAfter, defaultSeconds = DEFAULT_COOLDOWN_SECONDS) {
  if (retryAfter) {
    setCooldown(Number(retryAfter));
  } else {
    setCooldown(defaultSeconds);
  }
}
